/**
 * Página para registrar una atención clínica (Episodio + diagnósticos).
 *
 * El médico (Usuario logueado con Profesional vinculado) atiende al paciente.
 * Anamnesis, examen físico, dx CIE-10, indicaciones. Al cerrar, queda firmado
 * e inmutable.
 *
 * LO QUE EL PACIENTE SE LLEVA
 *
 * Antes había un solo campo "Plan" donde cabía todo: la receta, los exámenes y
 * el próximo control. Escrito ahí, nada de eso existía para el resto de la
 * clínica — farmacia no veía la receta, laboratorio no sabía qué procesar y
 * caja no tenía qué cobrar. Ahora cada cosa tiene su sitio y el texto libre
 * queda para lo que de verdad es texto: las indicaciones al paciente.
 *
 * Esas tres cosas cuelgan del episodio, así que necesitan que exista. Para no
 * obligar al médico a firmar antes de recetar, el primer clic guarda el
 * borrador solo: sigue escribiendo donde estaba, sin haber cerrado nada.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  FileSignature,
  Plus,
  Save,
  Search,
  Stethoscope,
  X,
} from "lucide-react";
import { getPaciente } from "../api/pacientes";
import {
  type Cie10Item,
  type DiagnosticoIn,
  type DiagnosticoCondicion,
  type DiagnosticoTipo,
  type EpisodioCreate,
  createEpisodio,
  getEpisodio,
  getHistoriaDePaciente,
  updateEpisodio,
  searchCie10,
} from "../api/clinical";
import { ApiError } from "../api/client";
import { getCita } from "../api/citas";
import { listTriajesDePaciente } from "../api/triaje";
import { useZonaHoraria, useBase } from "../auth/TenantBrandingProvider";
import { fechaHora, hora } from "../lib/fecha";
import { Alert, Button, Card, CardBody, CardHeader, Field } from "../components/ui";
import { TriajeResumen } from "../components/atencion/TriajeResumen";
import { FlujoAtencionDeCita } from "../components/atencion/FlujoAtencion";
import { RecetaEpisodio } from "../components/atencion/RecetaEpisodio";
import { OrdenLaboratorioEpisodio } from "../components/atencion/OrdenLaboratorio";
import { PlanTratamientoEpisodio } from "../components/atencion/PlanTratamiento";

/** Una parada del recorrido: quién la hizo y cuándo. */
function PasoPor({
  paso,
  quien,
  cuando,
}: {
  paso: string;
  quien: string | null | undefined;
  cuando: string | null;
}) {
  // Tres columnas de verdad: el nombre y la hora de cada paso quedan alineados
  // entre filas, que es lo que permite leerlas en vertical de un vistazo.
  return (
    <div className="grid grid-cols-[5rem_1fr_auto] items-baseline gap-x-3">
      <dt className="text-slate-500">{paso}</dt>
      <dd className="text-slate-800">
        {quien ?? <span className="text-slate-400">Sin registrar</span>}
      </dd>
      <dd className="whitespace-nowrap font-mono text-xs text-slate-400">
        {cuando ?? "—"}
      </dd>
    </div>
  );
}

interface DxSeleccionado {
  cie10: Cie10Item;
  tipo: DiagnosticoTipo;
  condicion: DiagnosticoCondicion;
}

export function AtenderPacientePage() {
  const { id } = useParams<{ slug: string; id: string }>();
  const base = useBase();
  const pacienteId = id ? Number(id) : NaN;
  const [searchParams] = useSearchParams();
  const citaId = searchParams.get("cita") ? Number(searchParams.get("cita")) : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tz = useZonaHoraria();

  const { data: paciente, isLoading: loadingPaciente } = useQuery({
    queryKey: ["paciente", pacienteId],
    queryFn: () => getPaciente(pacienteId),
    enabled: Number.isFinite(pacienteId),
  });

  // Borrador pendiente de este paciente.
  //
  // Guardar sin firmar deja el episodio ABIERTO, y la ficha decía "vuelve a
  // Atender para completarla" — pero Atender creaba OTRO episodio y el
  // borrador quedaba huérfano para siempre. Ahora se retoma: se carga su
  // contenido y al guardar se hace PATCH sobre el mismo episodio.
  // isFetched y no `historia`: un paciente sin historia devuelve null, y
  // esperar a que el dato sea "verdadero" dejaba el motivo sin heredar
  // justamente en la primera consulta de alguien.
  const { data: historia, isFetched: historiaLista } = useQuery({
    queryKey: ["historia", pacienteId],
    queryFn: () => getHistoriaDePaciente(pacienteId),
    enabled: Number.isFinite(pacienteId),
  });
  const borradorId = historia?.episodios.find((e) => e.estado === "ABIERTO")?.id ?? null;
  // Las cerradas y firmadas, de la más reciente a la más antigua. El borrador
  // que se está escribiendo no es "una atención anterior".
  const anteriores = (historia?.episodios ?? [])
    .filter((e) => e.id !== borradorId)
    .slice()
    .sort((a, b) => b.inicio.localeCompare(a.inicio));

  const { data: borrador } = useQuery({
    queryKey: ["episodio", borradorId],
    queryFn: () => getEpisodio(borradorId as number),
    enabled: borradorId != null,
  });

  // El triaje de ESTA cita. Es lo que el paciente ya contó en admisión y
  // enfermería confirmó: llega con el paciente, no se vuelve a preguntar.
  const { data: triajes = [] } = useQuery({
    queryKey: ["triajes", pacienteId],
    queryFn: () => listTriajesDePaciente(pacienteId),
    enabled: Number.isFinite(pacienteId),
  });
  const triaje = citaId != null ? triajes.find((t) => t.cita_id === citaId) ?? null : null;

  // La cita trae de quién viene: quién la registró en admisión y quién cobró.
  const { data: cita } = useQuery({
    queryKey: ["cita", citaId],
    queryFn: () => getCita(citaId as number),
    enabled: citaId != null,
  });

  const [motivo, setMotivo] = useState("");
  const [anamnesis, setAnamnesis] = useState("");
  const [examenFisico, setExamenFisico] = useState("");
  const [plan, setPlan] = useState("");
  const [diagnosticos, setDiagnosticos] = useState<DxSeleccionado[]>([]);
  // Arranca APAGADO. Venía encendido, así que el botón principal decía "Firmar
  // atención" desde el primer segundo — y firmar deja el episodio inmutable, o
  // sea sin poder recetar. Para eso se había metido un segundo botón de
  // guardar, que es lo que confundía: dos guardar en la misma pantalla y una
  // explicación de por qué hacía falta el primero.
  const [cerrar, setCerrar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cargado, setCargado] = useState<number | null>(null);
  const [heredado, setHeredado] = useState(false);
  // Borrador creado en esta sesión al pedir receta o exámenes. A partir de ahí
  // el formulario edita ESE episodio, no crea otro.
  const [episodioCreado, setEpisodioCreado] = useState<number | null>(null);
  const [guardadoEn, setGuardadoEn] = useState<string | null>(null);
  // Acuse de recibo que se apaga solo.
  //
  // El botón guardaba de verdad y la única señal era una línea gris al pie
  // que ya estaba ahí de antes: pulsar y que no se mueva nada se lee como que
  // no pasó nada, y el médico vuelve a pulsar o da por perdido lo escrito. El
  // aviso de arriba tampoco servía —esta pantalla es larga y al guardar se
  // está abajo, en el botón—, así que la confirmación va DONDE ESTÁ LA MANO y
  // se distingue de la marca de tiempo permanente por ser momentánea.
  const [acuse, setAcuse] = useState(false);

  useEffect(() => {
    if (!acuse) return;
    // Cinco segundos: los que hacen falta para levantar la vista y leerlo.
    // Eran ocho cuando el aviso vivía dentro del botón y había que esperar a
    // que se destapara para seguir; ahora no estorba a nadie, pero tampoco
    // tiene que quedarse ahí cuando ya se leyó.
    const t = setTimeout(() => setAcuse(false), 5000);
    return () => clearTimeout(t);
  }, [acuse]);
  const episodioActual = episodioCreado ?? borradorId;

  // Atención nueva: el motivo arranca con lo que dijo el paciente en triaje.
  // El médico lo corrige si hace falta, pero no parte de una hoja en blanco
  // teniendo el dato delante. Si hay borrador manda el borrador (más abajo):
  // es lo que él mismo escribió.
  if (triaje && historiaLista && borradorId == null && !heredado) {
    setHeredado(true);
    if (!motivo) setMotivo(triaje.motivo_consulta);
  }

  // Vuelca el borrador en el formulario una sola vez, sin pisar lo que el
  // profesional ya haya empezado a escribir en esta sesión.
  if (borrador && cargado !== borrador.id) {
    setCargado(borrador.id);
    setMotivo(borrador.motivo_consulta ?? "");
    setAnamnesis(borrador.anamnesis ?? "");
    setExamenFisico(borrador.examen_fisico ?? "");
    setPlan(borrador.plan ?? "");
    setDiagnosticos(
      borrador.diagnosticos.map((d) => ({
        // El episodio guardado no devuelve el capítulo CIE-10; solo se usa
        // para mostrar, así que no hace falta re-consultarlo.
        cie10: { codigo: d.cie10_codigo, descripcion: d.cie10_descripcion, capitulo: null },
        tipo: d.tipo,
        condicion: d.condicion,
      })),
    );
  }

  const mutation = useMutation({
    mutationFn: (payload: EpisodioCreate) =>
      episodioActual != null
        ? updateEpisodio(episodioActual, {
            motivo_consulta: payload.motivo_consulta,
            anamnesis: payload.anamnesis,
            examen_fisico: payload.examen_fisico,
            plan: payload.plan,
            diagnosticos: payload.diagnosticos,
            cerrar: payload.cerrar,
          })
        : createEpisodio(payload),
    onSuccess: (data) => {
      // Firmar cierra el trabajo y lleva a la atención terminada. Guardar un
      // borrador NO mueve de sitio: el médico le dio a guardar para no perder
      // lo escrito, no para irse a otra pantalla. Sacarlo de aquí lo obligaba a
      // buscar el camino de vuelta para seguir donde estaba.
      if (data.estado === "CERRADO") {
        // Se tira el episodio de la caché ANTES de ir a verlo.
        //
        // La pantalla de la atención lo pide con su propia clave, y si ya
        // estaba cacheado de cuando era borrador —lo está, porque al guardar
        // se pasa por ahí— la abre diciendo "Borrador" sobre una atención que
        // el servidor acaba de firmar. Nada estaba mal guardado: la pantalla
        // pintaba lo de hace un minuto.
        //
        // Con `historia` pasa lo mismo: el listado del paciente seguiría
        // marcándola sin firmar.
        queryClient.removeQueries({ queryKey: ["episodio", data.id] });
        queryClient.invalidateQueries({ queryKey: ["historia", pacienteId] });
        queryClient.invalidateQueries({ queryKey: ["citas"] });
        navigate(`${base}/episodios/${data.id}`, { replace: true });
        return;
      }
      setEpisodioCreado(data.id);
      // Ya está volcado en pantalla: que la recarga del borrador no lo pise.
      setCargado(data.id);
      setGuardadoEn(new Date().toISOString());
      setAcuse(true);
      // Al volver a entrar por Atender tiene que retomar ESTE borrador.
      queryClient.invalidateQueries({ queryKey: ["historia", pacienteId] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const detail = (err.payload as { detail?: string })?.detail;
        setError(detail ?? "No se pudo guardar la atención.");
      } else {
        setError("Error de conexión.");
      }
    },
  });

  const hasPrincipal = diagnosticos.some((d) => d.tipo === "PRINCIPAL");

  function datosDelFormulario(cerrarlo: boolean): EpisodioCreate {
    return {
      paciente_id: pacienteId,
      cita_id: citaId,
      motivo_consulta: motivo || null,
      anamnesis: anamnesis || null,
      examen_fisico: examenFisico || null,
      plan: plan || null,
      diagnosticos: diagnosticos.map<DiagnosticoIn>((d) => ({
        cie10_codigo: d.cie10.codigo,
        tipo: d.tipo,
        condicion: d.condicion,
      })),
      cerrar: cerrarlo,
    };
  }

  // Lo que impide firmar, dicho ANTES de pulsar y no después.
  //
  // El aviso salía al intentarlo y se pintaba arriba del todo, fuera de la
  // pantalla: desde el botón, firmar simplemente no hacía nada.
  const faltaParaFirmar =
    cerrar && !hasPrincipal
      ? "Falta marcar un diagnóstico como PRINCIPAL para poder firmar."
      : null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setAcuse(false);
    if (cerrar && !hasPrincipal) {
      setError("Para cerrar la atención necesitas al menos un diagnóstico marcado como PRINCIPAL.");
      return;
    }
    mutation.mutate(datosDelFormulario(cerrar));
  }

  function addDx(cie10: Cie10Item) {
    if (diagnosticos.find((d) => d.cie10.codigo === cie10.codigo)) return;
    const esElPrimero = diagnosticos.length === 0;
    setDiagnosticos([
      ...diagnosticos,
      {
        cie10,
        tipo: esElPrimero ? "PRINCIPAL" : "SECUNDARIO",
        condicion: "PRESUNTIVO",
      },
    ]);
  }

  function removeDx(codigo: string) {
    setDiagnosticos(diagnosticos.filter((d) => d.cie10.codigo !== codigo));
  }

  function updateDx(codigo: string, updates: Partial<DxSeleccionado>) {
    setDiagnosticos(
      diagnosticos.map((d) =>
        d.cie10.codigo === codigo ? { ...d, ...updates } : d,
      ),
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <Link
        to={`${base}/pacientes/${pacienteId}`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver al paciente
      </Link>

      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)] flex items-center justify-center">
          <Stethoscope className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {borradorId != null ? "Continuar atención" : "Nueva atención"}
          </h1>
          {loadingPaciente && <p className="text-sm text-slate-400 mt-1">Cargando paciente…</p>}
          {paciente && (
            <p className="text-sm text-slate-500 mt-1">
              Paciente: <span className="font-medium text-slate-900">{paciente.nombre_completo}</span>
              <span className="ml-2 font-mono text-slate-400">
                {paciente.tipo_documento} {paciente.num_documento}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* En qué estado está lo que se ve en pantalla.
          Antes solo avisaba si el borrador venía de otro día: quien acababa de
          guardar no recibía ninguna señal y no sabía si había guardado algo.
          Y no saber si tu registro clínico existe no es un detalle. */}
      {episodioActual != null && (
        <Alert variant="info">
          {guardadoEn ? (
            <>
              <span className="font-medium">
                Borrador guardado a las {hora(guardadoEn, tz)}
              </span>{" "}
              — todavía sin firmar. Sigue escribiendo: todo se guarda en esta
              misma atención, no se crea otra. Cuando termines, firma abajo.
            </>
          ) : (
            <>
              Estás retomando un borrador guardado{" "}
              {borrador?.inicio ? fechaHora(borrador.inicio, tz) : ""}. Al guardar
              se actualiza esa misma atención — no se crea otra.
            </>
          )}
        </Alert>
      )}

      {error && <Alert>{error}</Alert>}

      {/* Por dónde va el paciente. Lo primero de la pantalla: sitúa al médico
          antes de leer nada. */}
      {cita && (
        <Card>
          <CardBody>
            <FlujoAtencionDeCita cita={cita} />
          </CardBody>
        </Card>
      )}

      {/* Lo que ya se sabe del paciente antes de que abra la boca. Va arriba
          del formulario porque se lee antes de escribir nada. */}
      {triaje && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-slate-900">Llega de triaje</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Registrado antes de la consulta. Solo lectura.
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            <TriajeResumen triaje={triaje} fecha={fechaHora(triaje.fecha, tz)} />

            {/* Por qué manos pasó antes de llegar aquí. El médico que recibe a
                alguien con la presión rara o el motivo a medias necesita saber
                a quién preguntarle, y no tenerlo es lo que obliga a volver a
                interrogar al paciente. */}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Por dónde pasó
              </p>
              <dl className="mt-1.5 space-y-1 text-sm">
                <PasoPor
                  paso="Admisión"
                  quien={cita?.creado_por_nombre}
                  cuando={cita ? fechaHora(cita.created_at, tz) : null}
                />
                <PasoPor
                  paso="Caja"
                  quien={cita?.pago_registrado_por_nombre}
                  cuando={cita?.pago_fecha ? fechaHora(cita.pago_fecha, tz) : null}
                />
                <PasoPor
                  paso="Triaje"
                  quien={triaje.tomado_por_nombre}
                  cuando={fechaHora(triaje.fecha, tz)}
                />
              </dl>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Atenciones anteriores.
          Un paciente con veinte consultas encima no empieza de cero cada vez:
          el médico necesita ver qué se le diagnosticó antes SIN salir de esta
          pantalla y perder lo que lleva escrito. Se muestran las últimas; la
          historia completa está a un clic. */}
      {anteriores.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold text-slate-900">Atenciones anteriores</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                {anteriores.length === 1
                  ? "1 atención previa"
                  : `${anteriores.length} atenciones previas`}
                {historia?.numero ? ` · Historia ${historia.numero}` : ""}
              </p>
            </div>
            <Link
              to={`${base}/pacientes/${pacienteId}`}
              className="text-sm font-medium text-[color:var(--color-link)] hover:underline"
            >
              Ver historia completa →
            </Link>
            </div>
          </CardHeader>
          <CardBody>
            <ul className="divide-y divide-slate-100">
              {anteriores.slice(0, 5).map((ep) => (
                <li key={ep.id} className="flex flex-wrap items-baseline gap-x-3 py-2">
                  <span className="w-36 shrink-0 text-sm text-slate-500">
                    {fechaHora(ep.inicio, tz)}
                  </span>
                  <span className="text-sm text-slate-800">
                    {ep.especialidad_nombre ?? "—"}
                    <span className="text-slate-400"> · {ep.profesional_nombre}</span>
                  </span>
                  <span className="grow text-sm text-slate-700">
                    {ep.dx_principal ? (
                      <>
                        {ep.dx_principal_codigo && (
                          <span className="font-mono text-xs text-slate-500">
                            {ep.dx_principal_codigo}{" "}
                          </span>
                        )}
                        {ep.dx_principal}
                      </>
                    ) : (
                      <span className="text-slate-400">Sin diagnóstico principal</span>
                    )}
                  </span>
                  <Link
                    to={`${base}/episodios/${ep.id}`}
                    className="text-sm text-[color:var(--color-link)] hover:underline"
                  >
                    Abrir
                  </Link>
                </li>
              ))}
            </ul>
            {anteriores.length > 5 && (
              <p className="pt-2 text-xs text-slate-500">
                Se muestran las 5 más recientes de {anteriores.length}.
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {/* El id permite que el botón de firmar viva fuera del <form>: receta y
          exámenes son formularios propios y anidarlos aquí haría que cualquier
          botón suyo enviara la atención entera. */}
      <form id="atencion" onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-slate-900">Motivo de consulta y anamnesis</h2>
          </CardHeader>
          <CardBody className="space-y-4">
            <Field
              label="Motivo de consulta"
              htmlFor="motivo"
              hint={
                triaje
                  ? "Viene del triaje. Corrígelo si el paciente cuenta otra cosa."
                  : "Resumen breve de por qué viene el paciente."
              }
            >
              <textarea
                id="motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                maxLength={2000}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
              />
            </Field>
            <Field
              label="Anamnesis"
              htmlFor="anamnesis"
              hint="Tiempo de enfermedad, signos y síntomas, antecedentes relevantes."
            >
              <textarea
                id="anamnesis"
                value={anamnesis}
                onChange={(e) => setAnamnesis(e.target.value)}
                rows={6}
                maxLength={20000}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
              />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-semibold text-slate-900">Examen físico</h2>
          </CardHeader>
          <CardBody>
            <Field
              label="Hallazgos del examen físico"
              htmlFor="examen"
              hint="Signos vitales, examen segmentario, hallazgos relevantes."
            >
              <textarea
                id="examen"
                value={examenFisico}
                onChange={(e) => setExamenFisico(e.target.value)}
                rows={6}
                maxLength={20000}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
              />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">Diagnósticos (CIE-10)</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Marca uno como PRINCIPAL antes de cerrar la atención.
                </p>
              </div>
              {diagnosticos.length > 0 && (
                <span className="text-xs text-slate-500">
                  {diagnosticos.length} dx · {hasPrincipal ? "principal definido ✓" : "falta principal"}
                </span>
              )}
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            <Cie10Selector onAdd={addDx} />

            {diagnosticos.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">
                Busca un código CIE-10 arriba y haz click para agregarlo.
              </p>
            ) : (
              <div className="space-y-2">
                {diagnosticos.map((d) => (
                  <DxRow
                    key={d.cie10.codigo}
                    dx={d}
                    onUpdate={(updates) => updateDx(d.cie10.codigo, updates)}
                    onRemove={() => removeDx(d.cie10.codigo)}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-semibold text-slate-900">Indicaciones</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Lo que el paciente tiene que hacer en casa. La receta, los exámenes
              y el próximo control van abajo, cada uno en su sitio.
            </p>
          </CardHeader>
          <CardBody>
            <Field
              label="Indicaciones al paciente"
              htmlFor="plan"
              hint="Reposo, dieta, cuidados, señales de alarma por las que debe volver antes."
            >
              <textarea
                id="plan"
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                rows={5}
                maxLength={20000}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
              />
            </Field>
          </CardBody>
        </Card>
      </form>

      {/* Fuera del <form> a propósito: cada uno de estos bloques tiene sus
          propios botones y diálogos. */}
      {/* Lo que sale de la consulta: solo cuando hay consulta.

          Aquí hubo primero un botón extra para "preparar el borrador", y
          después un cartel anunciando las tres cosas que se habilitarían al
          guardar. Las dos versiones ocupaban media pantalla para hablar de algo
          que todavía no existía: el médico leía "Lo que el paciente se lleva"
          sin haber recetado nada. Un título que miente hasta que dejas de
          mirarlo no es interfaz.

          Mientras no hay atención guardada aquí no va NADA. Se guarda con el
          único botón del pie, y entonces aparecen los tres bloques, cada uno
          con su botón: emitir receta, solicitar exámenes, programar control.
          Ninguno es obligatorio. */}
      {episodioActual != null && (
        <section className="space-y-6">
          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Lo que el paciente se lleva
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Nada de esto es obligatorio. Lo que emitas llega solo a quien le
              toca: farmacia despacha, caja cobra, laboratorio procesa.
            </p>
          </div>

          <RecetaEpisodio episodioId={episodioActual} />
          <OrdenLaboratorioEpisodio episodioId={episodioActual} />
          <PlanTratamientoEpisodio episodioId={episodioActual} />
        </section>
      )}

      {/* Firmar no se ofrece hasta que hay atención guardada.

          Ofrecerlo antes es ofrecer cerrar la puerta sin haber entrado: al
          firmar el episodio queda inmutable, y el médico que todavía no ha
          recetado se queda sin poder hacerlo. */}
      {episodioActual != null && (
        /* EL ÚLTIMO PASO TIENE QUE PARECER EL ÚLTIMO PASO.

           Era una casilla gris del tamaño de cualquier otra cosa, perdida
           entre tres bloques, y lo que decide si la atención queda firmada o
           se queda en borrador para siempre. Quien no supiera que existe, no
           la veía. Ahora va en un bloque con su borde, su icono y su título,
           y cambia de color al marcarla: mientras está sin firmar se lee que
           falta algo. */
        <Card
          className={
            cerrar
              ? "border-2 border-[color:var(--color-link)]"
              : "border-2 border-dashed border-slate-300"
          }
        >
          <CardBody>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cerrar}
                onChange={(e) => setCerrar(e.target.checked)}
                className="mt-1 h-5 w-5 rounded border-slate-400 text-[color:var(--color-link)]"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FileSignature
                    className={`h-5 w-5 ${
                      cerrar ? "text-[color:var(--color-link)]" : "text-slate-400"
                    }`}
                    aria-hidden="true"
                  />
                  <p className="text-base font-semibold text-slate-900">
                    Firmar y cerrar la atención
                  </p>
                  {!cerrar && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      Falta este paso
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Al cerrar, el episodio queda inmutable: ya no se le pueden
                  añadir recetas ni exámenes. Déjalo sin marcar para seguir
                  completándola después.
                </p>
                {/* Junto a la casilla que lo provoca, no solo al pie: quien
                    acaba de marcarla está mirando aquí. */}
                {faltaParaFirmar && (
                  <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-red-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {faltaParaFirmar}
                  </p>
                )}
              </div>
            </label>
          </CardBody>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* La marca de tiempo permanente se queda a la izquierda: es
            referencia, no noticia. */}
        {guardadoEn && !acuse && (
          <span className="mr-auto text-sm text-slate-500">
            Guardado a las {hora(guardadoEn, tz)}
            {cerrar && " · falta firmar"}
          </span>
        )}

        <Link
          to={`${base}/pacientes/${pacienteId}`}
          className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
        >
          Cancelar
        </Link>

        {/* El acuse y el error van PEGADOS al botón: después de "Cancelar" y
            justo antes de él. Estaban con `mr-auto`, o sea en el borde
            izquierdo del pie; en una pantalla ancha eso queda a un palmo largo
            del cursor, y un aviso que aparece fuera de donde miras no existe. */}
        {acuse && (
          <span
            role="status"
            className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800"
          >
            <Check className="h-5 w-5 shrink-0" aria-hidden="true" />
            Borrador guardado a las {hora(guardadoEn, tz)}
            {cerrar && " · todavía sin firmar"}
          </span>
        )}

        {/* El mismo mensaje que arriba, repetido aquí a propósito: quien
            pulsa está al final de la página y nunca ve el de arriba. */}
        {(error || faltaParaFirmar) && (
          <span
            role="alert"
            className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700"
          >
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
            {error ?? faltaParaFirmar}
          </span>
        )}
        {/* EL BOTÓN NO SE CONVIERTE EN UN CARTEL.

            Se ponía verde y decía "Guardado" durante unos segundos, al lado
            del recuadro que ya decía lo mismo: dos avisos para una cosa. Y
            peor, era un botón que no se podía pulsar — quien quería seguir
            y firmar tenía que esperar a que se le pasara el disfraz.

            El aviso lo da el recuadro de al lado, que para eso está. El botón
            es un botón. */}
        <Button type="submit" form="atencion" loading={mutation.isPending}>
          {cerrar ? (
            <>
              <FileSignature className="w-4 h-4" />
              Firmar atención
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {episodioActual == null ? "Guardar y continuar" : "Guardar borrador"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------- CIE-10 selector ----------

function Cie10Selector({ onAdd }: { onAdd: (item: Cie10Item) => void }) {
  const [query, setQuery] = useState("");
  const { data = [], isFetching } = useQuery({
    queryKey: ["cie10", query],
    queryFn: () => searchCie10(query),
    enabled: query.length >= 1,
    staleTime: 60_000,
  });

  return (
    <div className="border border-slate-200 rounded-lg">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Busca por código (J06) o descripción (resfriado, cefalea, lumbago…)"
          className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-slate-400"
        />
        {isFetching && <span className="text-xs text-slate-400">…</span>}
      </div>
      {query.length >= 1 && (
        <div className="max-h-60 overflow-y-auto">
          {data.length === 0 && !isFetching && (
            <p className="text-xs text-slate-400 text-center py-4">Sin resultados.</p>
          )}
          {data.map((item) => (
            <button
              key={item.codigo}
              type="button"
              onClick={() => {
                onAdd(item);
                setQuery("");
              }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-left border-b border-slate-100 last:border-0"
            >
              <Plus className="w-3.5 h-3.5 text-[color:var(--color-link)]" />
              <span className="font-mono text-xs text-slate-700 w-16">{item.codigo}</span>
              <span className="flex-1 text-sm text-slate-900">{item.descripcion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DxRow({
  dx,
  onUpdate,
  onRemove,
}: {
  dx: DxSeleccionado;
  onUpdate: (u: Partial<DxSeleccionado>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg">
      <span className="font-mono text-xs text-slate-700 w-16">{dx.cie10.codigo}</span>
      <span className="flex-1 text-sm text-slate-900">{dx.cie10.descripcion}</span>
      <select
        value={dx.tipo}
        onChange={(e) => onUpdate({ tipo: e.target.value as DiagnosticoTipo })}
        className="rounded border border-slate-300 px-2 py-1 text-xs"
      >
        <option value="PRINCIPAL">Principal</option>
        <option value="SECUNDARIO">Secundario</option>
      </select>
      <select
        value={dx.condicion}
        onChange={(e) => onUpdate({ condicion: e.target.value as DiagnosticoCondicion })}
        className="rounded border border-slate-300 px-2 py-1 text-xs"
      >
        <option value="PRESUNTIVO">Presuntivo</option>
        <option value="DEFINITIVO">Definitivo</option>
      </select>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 text-slate-400 hover:text-red-600"
        aria-label="Quitar"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
